import { SiteConfig } from '@99designs/common/apps/global-nav/lib/Site';
import bugsnagReact from '@bugsnag/plugin-react';
import { loadableReady } from '@loadable/component';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';

import 'isomorphic-fetch';
import React, { useEffect } from 'react';
import { ApolloProvider } from '@apollo/react-components';
import { hydrate } from 'react-dom';
import { hot } from 'react-hot-loader';
import { addLocaleData } from 'react-intl';
import { ThemeProvider } from '@material-ui/core/styles';
import { BrowserRouter } from 'react-router-dom';
// @ts-ignore
import localeData from 'ReactIntlLocaleData'; // eslint-disable-line import/no-unresolved

import createBrowserClient from 'common/ApolloClient/createBrowserClient';
import bugsnagClient from 'common/Bugsnag/Bugsnag';
import { ExperimentsData, ExperimentsProvider } from 'common/ExperimentsContext';
import { AccountsJs } from './AccountsJs';
import App from './App';
import materialTheme from './materialTheme';
import { HelmetProvider } from 'react-helmet-async';

export interface ApolloAwareWindow extends Window {
    apolloInitState: NormalizedCacheObject;
    init: () => void;
    initialised: boolean;
}

// Setup here
const backend = createBrowserClient({
    endpoint: '/app/graphql',
    cacheDiv: 'apollo-init-data-backend',
});

const siteDataNode = document.getElementById('site-init-data');
const siteState = JSON.parse(siteDataNode!.textContent!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

const experimentsDataNode = document.getElementById('experiments-init-data');
const experimentsData: ExperimentsData =
    experimentsDataNode && experimentsDataNode.textContent
        ? JSON.parse(experimentsDataNode.textContent)
        : { experiments: '' };

const HotReloadableRoot = hot(module)(
    ({ language, siteConfig }: { language: string; siteConfig: SiteConfig }) => {
        useEffect(() => {
            const jssStyles = document.querySelector('#jss-server-side');
            if (jssStyles && jssStyles.parentElement) {
                jssStyles.parentElement.removeChild(jssStyles);
            }
        }, []);
        return (
            <ThemeProvider theme={materialTheme}>
                <App
                    language={language}
                    siteConfig={siteConfig}
                    backendSubscriptionClient={backend.subscriptionClient}
                />
                <AccountsJs />
            </ThemeProvider>
        );
    }
);

const renderApp = () => {
    const htmlNode = document.getElementsByTagName('html');
    const language = htmlNode[0].getAttribute('lang')!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

    // Initialise react-intl locale
    addLocaleData(localeData);

    bugsnagClient.use(bugsnagReact, React);
    const BugsnagErrorBoundary = bugsnagClient.getPlugin('react');

    hydrate(
        <BugsnagErrorBoundary>
            <ApolloProvider client={backend.combinedClient}>
                <ExperimentsProvider value={experimentsData}>
                    <BrowserRouter>
                        <HelmetProvider>
                            <HotReloadableRoot language={language} siteConfig={siteState} />
                        </HelmetProvider>
                    </BrowserRouter>
                </ExperimentsProvider>
            </ApolloProvider>
        </BugsnagErrorBoundary>,
        document.getElementById('root'),
        () => {
            (window as ApolloAwareWindow).initialised = true;
        }
    );
};

if (!window.Intl) {
    document.write(
        '<script src="https://assets.99static.com/shared/js/Intl.min-856e66ee99.js"></script>'
    );
}

(window as ApolloAwareWindow).init = () => {
    loadableReady(() => {
        renderApp();
    });
};

if (module.hot) {
    module.hot.accept();
}
